// Angular imports
import {
    Component,
    OnInit,
    Input,
    EventEmitter,
    Output,
    ViewChild,
    OnDestroy,
    OnChanges,
    SimpleChanges,
    AfterViewInit
} from '@angular/core';

// 3rd party imports
import { forkJoin, Observable, Subscription } from 'rxjs';
import { alert } from 'devextreme/ui/dialog';

// CC-Common imports
import { APP_VERSION, CCLocalStorageCategory, CCLocalStorageService, Utils } from 'cc-common';

// Component imports
import { DxScrollViewComponent, DxTreeViewComponent } from 'devextreme-angular';

// Service imports
import { ScreenService } from '../../_services/shared-services/screen.service';
import { NavigationService } from '../../_services/shared-services/navigation.service';
import { ContextManagerService } from '../../_services/data-services/context-manager.service';
import { HelperService } from '../../_services/shared-services/helper.service';

// Model imports
import { SideNavTreeInfo } from '../../_models/side-nav-tree-info';
import { PropertyCommunityInfoResultDto } from '../../_models/property-community-info-result-dto';

// Constant
import { ImageConstants } from '../../_constants/image-constants';
import { StorageKeyConstants } from '../../_constants/storage-key-constants';
import { SideNavigationConstants } from '../../_constants/navigation-constants';

@Component({
    selector: 'app-side-navigation-menu',
    templateUrl: './side-navigation-menu.component.html',
    styleUrls: ['./side-navigation-menu.component.scss']
})
export class SideNavigationMenuComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
    @ViewChild('topTreeView', { static: false }) topTreeView: DxTreeViewComponent;
    @ViewChild('topScrollView', { static: false }) topScrollView: DxScrollViewComponent;

    @Input() topItems: SideNavTreeInfo[];
    @Input() bottomItems: SideNavTreeInfo[];
    @Input() selectedPath: string;
    @Input() itemToSelect: string;
    @Input() compactMode: boolean;

    @Output() selectedItemChanged = new EventEmitter<object>();
    @Output() expandSideNavigator = new EventEmitter<boolean>();

    isLoadComplete = false;
    isSmallScreen = false;
    isXSmallScreen = false;
    hasHeight = false;
    ccLogo: string = ImageConstants.ccLogo;
    poweredByCCLogo: string = ImageConstants.poweredByCCLogo;
    searchText = '';
    timeoutValue = 50;
    appVersion = APP_VERSION;

    selectedContext: PropertyCommunityInfoResultDto;
    selectedNode: SideNavTreeInfo;
    selectedWorkspaceNode: SideNavTreeInfo;

    contextChangeSubscription: Subscription;
    navigationChangeSubscription: Subscription;
    workspaceSelectSubscription: Subscription;
    workspaceViewSubscription: Subscription;
    screenSizeSubscription: Subscription;
    sideNavSizeSubscription: Subscription;
    viewSubscriptions: Array<Subscription> = [];

    today = new Date();
    currentYear: number;
    timerId: any;
    viewTimerIds: Array<any> = [];

    constructor(
        private readonly screenService: ScreenService,
        private readonly navigationService: NavigationService,
        private readonly contextManagerService: ContextManagerService,
        private readonly helperService: HelperService,
        private readonly localStorageService: CCLocalStorageService
    ) {}

    get showScrollbar(): string {
        return this.compactMode
            ? 'never'
            : this.localStorageService.getItem(StorageKeyConstants.userScrollbarKey, CCLocalStorageCategory.PREF);
    }

    ngOnInit() {
        this.currentYear = this.today.getFullYear();
        this.updateScreenSize();
        this.screenSizeSubscription = this.screenService.resized.subscribe(() => {
            this.updateScreenSize();
        });

        this.updateSelectedContext();
        this.contextChangeSubscription = this.contextManagerService.getContext().subscribe(() => {
            this.updateSelectedContext();
        });

        this.navigationChangeSubscription = this.helperService.subjectNavigationChange.subscribe(
            (selectPath: string) => {
                this.onNavigationChange(selectPath);
            }
        );

        this.workspaceSelectSubscription = this.navigationService
            .onWorkspaceSelection()
            .subscribe((item: SideNavTreeInfo) => {
                const parentNode = this.navigationService.getSideNavItemById(this.topItems, item.parentId);
                this.onTopItemClick(parentNode);
            });

        this.workspaceViewSubscription = this.navigationService.onWorkspaceView().subscribe((item: SideNavTreeInfo) => {
            this.selectedWorkspaceNode = this.navigationService.selectWorkspaceItem(this.topItems, item);
        });

        this.viewSubscriptions.push(
            this.navigationService.getSearchedItem().subscribe((text: string) => {
                this.searchText = (text || '').trim();
                this.searchInTopMenuItems();
            })
        );

        this.sideNavSizeSubscription = this.helperService.subjectSideNavSizeChanged.subscribe(() => {
            this.viewTimerIds.push(
                setTimeout(() => {
                    try {
                        this.updateNavigationTreeView();
                    } catch {
                        alert(
                            `An internal error occurred with the side navigation menu. Please reload / refresh your browser window to fix this error`,
                            'Sidenav Error'
                        );
                    }
                }, this.timeoutValue + 450)
            );
        });
    }

    ngAfterViewInit(): void {
        this.isLoadComplete = true;
        // [DEV-3990] When user browser resized, drawer control destroys side nav menu and recreates.
        // ngAfterViewInit is called when browser resized. Below code block is added highlight
        // the selected menu item in side navigation tree.
        // Using selectedPath as the URL did not change in browser resize action.
        if (Utils.isFalse(this.compactMode) && Utils.notNullOrEmpty(this.selectedPath)) {
            this.viewTimerIds.push(setTimeout(() => this.onNavigationChange(this.selectedPath), 1000));
        } else {
            this.viewTimerIds.push(setTimeout(this.onTreeItemExpanded.bind(this), 1000));
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        if (this.topItems.length > 0 && this.isLoadComplete) {
            if (changes.topItems && changes.topItems.currentValue) {
                this.onNavigationChange(this.selectedPath);
            } else if (changes.selectedPath && changes.selectedPath.currentValue) {
                this.selectSideNavigationItem(changes.selectedPath.currentValue);
            } else if (changes.compactMode) {
                this.helperService.subjectSideNavSizeChanged.next(!changes.compactMode.currentValue);
            }
        }
    }

    ngOnDestroy() {
        clearTimeout(this.timerId);
        this.contextChangeSubscription.unsubscribe();
        this.navigationChangeSubscription.unsubscribe();
        this.workspaceSelectSubscription.unsubscribe();
        this.workspaceViewSubscription.unsubscribe();
        this.screenSizeSubscription.unsubscribe();
        this.sideNavSizeSubscription.unsubscribe();
        this.viewSubscriptions.forEach((subscription) => subscription.unsubscribe());
        this.viewTimerIds.forEach((timerId) => clearTimeout(timerId));
    }

    isInDevOrQaRelease(item: SideNavTreeInfo): boolean {
        return Utils.isInList(
            item.name.toUpperCase(),
            SideNavigationConstants.QA_ACTION_ACCESS.concat(SideNavigationConstants.IT_ACTION_ACCESS)
        );
    }

    isInQaRelease(item: SideNavTreeInfo): boolean {
        return Utils.isInList(item.name.toUpperCase(), SideNavigationConstants.QA_ACTION_ACCESS);
    }

    isInDevRelease(item: SideNavTreeInfo): boolean {
        return Utils.isInList(item.name.toUpperCase(), SideNavigationConstants.IT_ACTION_ACCESS);
    }

    isWorkspaceLeaf(item: SideNavTreeInfo): boolean {
        return item.isWorkspace && item.items.length === 0 && !item.path;
    }

    hasExpandedItems(item: SideNavTreeInfo): boolean {
        return item.items.length > 0 && item.expanded && !item.path;
    }

    hasCollapsedItems(item: SideNavTreeInfo): boolean {
        return item.items.length > 0 && !item.expanded && !item.path;
    }

    refreshTopTree(): void {
        if (this.isLoadComplete && !!this.topTreeView && !!this.topTreeView.instance) {
            this.topTreeView.instance.option('dataSource', this.topItems);
            if (!!this.selectedNode) {
                this.onTopItemClick(this.selectedNode);
                if (!!this.selectedWorkspaceNode) {
                    this.navigationService.selectWorkspaceItem(this.topItems, this.selectedWorkspaceNode);
                }
            }
        }
    }

    onTreeItemClick(e: { itemData: SideNavTreeInfo }): void {
        if (this.navigationService.hasTrackUnsavedChanges()) {
            this.viewSubscriptions.push(
                this.navigationService.trackUnsavedChanges().subscribe((flag) => {
                    if (Utils.isTrue(flag)) {
                        this.processTreeItemClick(e);
                    }
                })
            );
        } else {
            this.processTreeItemClick(e);
        }
    }

    onTreeItemExpanded(): void {
        if (!this.screenService.isLargeScreen() || this.compactMode) {
            clearTimeout(this.timerId);
            this.timerId = setTimeout(() => {
                if (this.compactMode) {
                    this.topTreeView.instance.collapseAll();
                }
            }, 1000);
        }
    }

    onTopItemClick(itemData: SideNavTreeInfo): void {
        if (this.navigationService.hasTrackUnsavedChanges()) {
            this.viewSubscriptions.push(
                this.navigationService.trackUnsavedChanges().subscribe((flag) => {
                    if (Utils.isTrue(flag)) {
                        this.processTopItemClick(itemData);
                    }
                })
            );
        } else {
            this.processTopItemClick(itemData);
        }
    }

    onBottomItemClick(itemData: SideNavTreeInfo): void {
        if (this.navigationService.hasTrackUnsavedChanges()) {
            this.viewSubscriptions.push(
                this.navigationService.trackUnsavedChanges().subscribe((flag) => {
                    if (Utils.isTrue(flag)) {
                        this.processBottomItemClick(itemData);
                    }
                })
            );
        } else {
            this.processBottomItemClick(itemData);
        }
    }

    onNavigationChange(path: string): void {
        if (!!path && path.length > 0) {
            this.viewSubscriptions.push(
                this.clearMenuSelection().subscribe(() => {
                    this.viewTimerIds.push(setTimeout(() => this.selectSideNavigationItem(path), this.timeoutValue));
                })
            );
        }
    }

    selectPreviousSelectedItem(selectedPath: string): void {
        this.onNavigationChange(selectedPath || this.selectedPath);
    }

    getCCPoweredByStyle(): any {
        if (this.isXSmallScreen) {
            return { width: '145px' };
        }
        return {};
    }

    private processTreeItemClick(e: { itemData: SideNavTreeInfo }): void {
        if (
            (this.compactMode && !this.selectedNode) ||
            (!!this.selectedNode && this.selectedNode.id !== e.itemData.id)
        ) {
            const parentItems = this.navigationService.getParentItems(this.topItems, e.itemData);
            if (e.itemData.items.length > 0 || parentItems.length > 0) {
                this.expandSideNavigator.emit(true);
            }
        }
    }

    private processTopItemClick(itemData: SideNavTreeInfo): void {
        if (!this.selectedNode || this.selectedNode.id !== itemData.id) {
            if (!!itemData.path) {
                const scrollTop = this.topScrollView.instance.scrollTop();
                this.viewSubscriptions.push(
                    this.resetMenuSelection(this.bottomItems, null).subscribe(() => {
                        this.updateParentSideNavItems(itemData);
                        this.selectedNode = itemData;
                        this.selectedItemChanged.emit({ itemData });
                        this.viewTimerIds.push(
                            setTimeout(() => {
                                this.topTreeView.instance.option('dataSource', this.topItems);
                                this.topTreeView.instance.selectItem(itemData.id);
                                this.topTreeView.instance.repaint();
                                this.topScrollView.instance.scrollBy(scrollTop);

                                if (itemData.isWorkspace && itemData.items.length > 0) {
                                    this.navigationService.showSideNavItems([itemData], this.selectedNode);
                                    this.updateParentSideNavItems(itemData, 1);
                                    this.viewTimerIds.push(
                                        setTimeout(() => {
                                            this.topTreeView.instance.expandItem(itemData.id);
                                            this.selectedWorkspaceNode = this.navigationService.selectWorkspaceItem(
                                                this.topItems,
                                                itemData.items[0]
                                            );
                                            this.topTreeView.instance.repaint();
                                        }, this.timeoutValue)
                                    );
                                }
                            }, this.timeoutValue)
                        );
                    })
                );
            } else if (itemData.items.length > 0) {
                this.navigationService.hideSideNavItems(this.topItems);
                if (itemData.expanded) {
                    const collapsedItems = this.navigationService.collapseSideNavItems([itemData]);
                    collapsedItems.forEach((item) => {
                        this.topTreeView.instance.collapseItem(item.id);
                    });
                    this.updateParentSideNavItems(itemData);
                    this.viewTimerIds.push(
                        setTimeout(() => {
                            this.topTreeView.instance.collapseItem(itemData.id);
                            this.topTreeView.instance.repaint();
                        }, this.timeoutValue)
                    );
                } else {
                    this.viewSubscriptions.push(
                        this.resetMenuSelection(this.topItems, null).subscribe(() => {
                            const parentItems = this.navigationService.getParentItems(
                                [itemData],
                                this.selectedNode,
                                true
                            );
                            this.navigationService.showSideNavItems([itemData], this.selectedNode);
                            if (parentItems.length > 0) {
                                const expandedItems = this.navigationService.expandSideNavItems(
                                    parentItems,
                                    this.selectedNode
                                );
                                expandedItems.forEach((r) => this.topTreeView.instance.expandItem(r.id));
                                this.updateParentSideNavItems(this.selectedNode);
                                this.viewTimerIds.push(
                                    setTimeout(() => {
                                        this.topTreeView.instance.option('dataSource', this.topItems);
                                        this.topTreeView.instance.expandItem(itemData.id);
                                        this.selectedWorkspaceNode = this.navigationService.selectWorkspaceItem(
                                            this.topItems,
                                            this.selectedNode
                                        );
                                        this.topTreeView.instance.repaint();
                                    }, this.timeoutValue)
                                );
                            } else {
                                this.updateParentSideNavItems(itemData, 1);
                                this.viewTimerIds.push(
                                    setTimeout(() => {
                                        this.topTreeView.instance.expandItem(itemData.id);
                                        this.topTreeView.instance.repaint();
                                    }, this.timeoutValue)
                                );
                            }
                        })
                    );
                }
            } else if (!!itemData.parentId && !!itemData.id) {
                this.selectedWorkspaceNode = this.navigationService.selectWorkspaceItem(this.topItems, itemData);
                this.navigationService.updateWorkspaceSelection(itemData);
            }
        } else if (itemData.items.length > 0 && !itemData.expanded) {
            this.navigationService.showSideNavItems([itemData], this.selectedNode);
            this.viewTimerIds.push(
                setTimeout(() => {
                    this.topTreeView.instance.expandItem(itemData.id);
                    this.navigationService.selectWorkspaceItem(this.topItems, this.selectedWorkspaceNode);
                    this.topTreeView.instance.repaint();
                }, this.timeoutValue)
            );
        }

        if (this.compactMode) {
            this.onTreeItemExpanded();
        }
    }

    private processBottomItemClick(itemData: SideNavTreeInfo): void {
        if (!this.selectedNode || this.selectedNode.id !== itemData.id) {
            if (!!itemData.path) {
                this.viewSubscriptions.push(
                    this.resetMenuSelection(this.topItems, this.topTreeView).subscribe(() => {
                        this.selectedNode = itemData;
                        this.selectedItemChanged.emit({ itemData });
                    })
                );
            }
        } else if (Utils.isTrue(itemData.openInNewWindow)) {
            this.selectedItemChanged.emit({ itemData });
        }
    }

    private updateScreenSize(): void {
        this.hasHeight = this.screenService.hasMinHeight();
        this.isSmallScreen = this.screenService.isSmallScreen();
        this.isXSmallScreen = this.screenService.isXSmallScreen();
    }

    private updateSelectedContext(): void {
        this.selectedContext = this.contextManagerService.getSelectedContext();
    }

    private searchInTopMenuItems(): void {
        this.viewSubscriptions.push(
            this.clearMenuSelection().subscribe(() => {
                if (this.searchText.length > 0) {
                    this.navigationService.hideSideNavItems(this.topItems);
                    this.filterSideNavItems(this.topItems, this.searchText);
                    setTimeout(() => this.topTreeView.instance.repaint(), this.timeoutValue);
                } else {
                    this.onTopItemClick(this.selectedNode);
                }
            })
        );
    }

    private clearMenuSelection(): Observable<boolean[]> {
        return forkJoin([
            this.resetMenuSelection(this.topItems, this.topTreeView),
            this.resetMenuSelection(this.bottomItems, null)
        ]);
    }

    private resetMenuSelection(
        menuItems: SideNavTreeInfo[],
        treeView: DxTreeViewComponent,
        visible = true
    ): Observable<boolean> {
        return new Observable((observer) => {
            if (!!treeView && !!treeView.instance) {
                treeView.instance.beginUpdate();
            }
            this.navigationService.resetSideNavItems(menuItems, visible);
            setTimeout(() => {
                if (!!treeView && !!treeView.instance) {
                    treeView.instance.option('dataSource', menuItems);
                    treeView.instance.endUpdate();
                }
                observer.next(true);
                observer.complete();
            }, this.timeoutValue);
        });
    }

    private filterSideNavItems(items: SideNavTreeInfo[], text: string): void {
        items.forEach((item) => {
            const matched: boolean =
                item.text.toLowerCase().indexOf(text.toLowerCase()) !== -1 ||
                (item.path || '').toLowerCase().indexOf(text.toLowerCase()) !== -1;
            if (matched) {
                const parentItems = this.navigationService.getParentItems(this.topItems, item, true, [], this.topItems);
                const expandedItems = this.navigationService.expandSideNavItems(parentItems, this.selectedNode);
                expandedItems.forEach((r) => this.topTreeView.instance.expandItem(r.id));
                this.navigationService.showSideNavItems([item], this.selectedNode, false);
            } else {
                this.filterSideNavItems(item.items, text);
            }
        });
    }

    private updateParentSideNavItems(itemData: SideNavTreeInfo, level = 0): void {
        const parentItems = this.navigationService.getParentItems(this.topItems, itemData);
        if (parentItems.length > 0) {
            if (level === 0) {
                this.navigationService.hideSideNavItems(this.topItems);
            }
            this.navigationService.showSideNavItems(parentItems, this.selectedNode, level === 0);
            parentItems.forEach((parentData) => {
                this.updateParentSideNavItems(parentData, 1);
                this.viewTimerIds.push(
                    setTimeout(() => this.topTreeView.instance.expandItem(parentData.id), this.timeoutValue)
                );
            });
        } else if (level === 0) {
            this.navigationService.resetSideNavItems(this.topItems);
        }
    }

    private selectSideNavigationItem(selectedPath: string): void {
        if (!!selectedPath) {
            let selectedNode = this.navigationService.getSelectedSideNavItem(
                this.topItems,
                this.navigationService.getURLPath(selectedPath, this.selectedContext)
            );
            if (!!selectedNode && !!this.topTreeView && !!this.topTreeView.instance) {
                this.onTopItemClick(selectedNode);
            } else {
                selectedNode = this.navigationService.getSelectedSideNavItem(
                    this.bottomItems,
                    this.navigationService.getURLPath(selectedPath, this.selectedContext)
                );
                if (!!selectedNode) {
                    this.onBottomItemClick(selectedNode);
                }
            }
        }
    }

    private updateNavigationTreeView(): void {
        if (this.compactMode) {
            this.topTreeView.instance.collapseAll();
        } else if (this.selectedNode) {
            this.topTreeView.instance.expandItem(this.selectedNode.id);
            this.navigationService.showSideNavItems(this.topItems, this.selectedNode);
            if (!!this.selectedWorkspaceNode) {
                this.navigationService.selectWorkspaceItem(this.topItems, this.selectedWorkspaceNode);
            }
            // If there is a selected path but no selected node,
            // then use that for highlighting selected manu item
        } else if (Utils.notNullOrEmpty(this.selectedPath)) {
            this.onNavigationChange(this.selectedPath);
        }
    }
}
